/**
 * Cart Drawer Upsells — The Littl
 * Fetches product recommendations based on cart items and renders upsell cards.
 * Uses Shopify's native Product Recommendations API with complementary intent
 * for higher conversion (pairs well with vs generic related).
 */

class CartUpsells extends HTMLElement {
  constructor() {
    super();
    this.cartItemIds = [];
    this.rendered = false;
  }

  connectedCallback() {
    this.loadUpsells();
  }

  async loadUpsells() {
    const cartItemIds = this.getCartProductIds();
    if (cartItemIds.length === 0) {
      this.innerHTML = '';
      return;
    }

    try {
      // Fetch complementary (pairs well) + related recommendations in parallel
      // for better coverage — complementary converts higher but may return fewer results
      const [complementary, related] = await Promise.all([
        this.fetchRecommendations(cartItemIds[0], 'complementary', 6),
        this.fetchRecommendations(cartItemIds[0], 'related', 8),
      ]);

      const cartVariantIds = this.getCartVariantIds();
      const excludedTypes = ['gift box', 'packaging', 'gift wrap', 'gift card', 'gift cards'];
      const excludedTerms = ['gift box', 'packaging', 'gift wrap', 'wrapping', 'gwp:', 'free mini'];

      const filterProducts = (products) =>
        products.filter((p) => {
          if (!p.available) return false;
          if (cartItemIds.includes(String(p.id))) return false;
          if (cartVariantIds.includes(String(p.variants[0]?.id))) return false;
          const pType = (p.product_type || '').toLowerCase();
          if (excludedTypes.some((t) => pType.includes(t))) return false;
          const pTitle = (p.title || '').toLowerCase();
          if (excludedTerms.some((t) => pTitle.includes(t))) return false;
          if (!p.product_type || p.product_type.trim() === '') return false;
          return true;
        });

      const filteredComplementary = filterProducts(complementary);
      const filteredRelated = filterProducts(related);

      // Prioritise complementary products, fill remaining slots with related
      const seen = new Set();
      const merged = [];

      for (const p of [...filteredComplementary, ...filteredRelated]) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        merged.push(p);
        if (merged.length >= 6) break;
      }

      // Sort by price ascending — lower-priced items are easier impulse adds
      merged.sort((a, b) => a.variants[0].price - b.variants[0].price);

      if (merged.length === 0) {
        this.innerHTML = '';
        return;
      }

      const hasComplementary = filteredComplementary.length > 0;
      this.renderUpsells(merged, hasComplementary);
    } catch (e) {
      console.error('Cart upsells error:', e);
    }
  }

  async fetchRecommendations(productId, intent, limit) {
    try {
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`
      );
      if (!response.ok) return [];
      const data = await response.json();
      return data.products || [];
    } catch {
      return [];
    }
  }

  getCartProductIds() {
    const items = this.querySelectorAll('[data-upsell-product-id]');
    if (items.length > 0) {
      return Array.from(items).map((el) => el.dataset.upsellProductId);
    }
    const ids = [];
    document.querySelectorAll('.cart-item[data-product-id]').forEach((el) => {
      ids.push(el.dataset.productId);
    });
    return ids;
  }

  getCartVariantIds() {
    const ids = [];
    document.querySelectorAll('[data-quantity-variant-id]').forEach((el) => {
      ids.push(el.dataset.quantityVariantId);
    });
    return ids;
  }

  renderUpsells(products, hasComplementary) {
    // Use "Complete Your Look" when we have complementary matches,
    // "You May Also Like" as fallback
    const title = hasComplementary ? 'Complete Your Look' : 'You May Also Like';

    const scrollHTML = products
      .map((p) => {
        const variant = p.variants[0];
        const price = this.formatMoney(variant.price);
        const comparePrice = variant.compare_at_price && variant.compare_at_price > variant.price
          ? this.formatMoney(variant.compare_at_price)
          : null;
        const image = p.featured_image
          ? this.getSizedImage(p.featured_image, '200x200')
          : '';

        return `
          <div class="cart-upsell-card">
            <a href="${p.url}" tabindex="-1">
              ${image ? `<img class="cart-upsell-card__image" src="${image}" alt="${this.escapeHtml(p.title)}" loading="lazy" width="130" height="130">` : ''}
            </a>
            <div class="cart-upsell-card__info">
              <span class="cart-upsell-card__name">${this.escapeHtml(p.title)}</span>
              <span class="cart-upsell-card__price">
                ${comparePrice ? `<s class="cart-upsell-card__compare-price">${comparePrice}</s> ` : ''}${price}
              </span>
            </div>
            <button class="cart-upsell-card__add"
              data-variant-id="${variant.id}"
              onclick="cartUpsellAdd(this, ${variant.id})">
              + Add
            </button>
          </div>
        `;
      })
      .join('');

    this.innerHTML = `
      <p class="cart-upsells__title">${title}</p>
      <div class="cart-upsells__scroll">${scrollHTML}</div>
    `;
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `$${amount} AUD`;
  }

  getSizedImage(url, size) {
    if (!url) return '';
    const match = url.match(/(.+)\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
    if (match) {
      return `${match[1]}_${size}.${match[2]}${match[3] || ''}`;
    }
    return url;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define('cart-upsells', CartUpsells);

/**
 * Add upsell product to cart
 */
async function cartUpsellAdd(button, variantId) {
  button.classList.add('cart-upsell-card__add--adding');
  button.textContent = 'Adding...';

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: ['cart-drawer', 'cart-icon-bubble'],
      }),
    });

    if (!response.ok) throw new Error('Add failed');

    const data = await response.json();

    button.classList.remove('cart-upsell-card__add--adding');
    button.classList.add('cart-upsell-card__add--added');
    button.textContent = 'Added ✓';

    // Update cart drawer contents
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && data.sections) {
      cartDrawer.renderContents({ sections: data.sections, id: variantId });
    }

    // Publish cart update event for other listeners
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', cartData: data });
    }
  } catch (e) {
    button.classList.remove('cart-upsell-card__add--adding');
    button.textContent = '+ Add';
    console.error('Upsell add error:', e);
  }
}

/**
 * Shipping bar update — called after cart drawer re-renders
 */
function updateShippingBar() {
  const bar = document.querySelector('[data-shipping-bar]');
  if (!bar) return;

  const threshold = parseInt(bar.dataset.shippingThreshold) || 16000;
  const expressThreshold = parseInt(bar.dataset.expressThreshold) || 24900;
  const totalEl = document.querySelector('.totals__total-value');
  if (!totalEl) return;

  const totalText = totalEl.textContent.replace(/[^0-9.]/g, '');
  const totalCents = Math.round(parseFloat(totalText) * 100);
  const remaining = threshold - totalCents;
  const expressRemaining = expressThreshold - totalCents;
  const pct = Math.min(100, (totalCents / expressThreshold) * 100);

  const textEl = bar.querySelector('.cart-shipping-bar__text');
  const fillEl = bar.querySelector('.cart-shipping-bar__fill');

  if (expressRemaining <= 0) {
    textEl.innerHTML = '<strong>You\'ve unlocked FREE AU express shipping!</strong> 🎉';
    textEl.classList.add('cart-shipping-bar__text--success');
    fillEl.style.width = '100%';
    fillEl.classList.add('cart-shipping-bar__fill--complete');
    fillEl.classList.remove('cart-shipping-bar__fill--standard');
  } else if (remaining <= 0) {
    textEl.innerHTML = '<strong>You\'ve unlocked FREE shipping!</strong> 🎉';
    textEl.classList.add('cart-shipping-bar__text--success');
    fillEl.style.width = '100%';
    fillEl.classList.add('cart-shipping-bar__fill--complete');
    fillEl.classList.remove('cart-shipping-bar__fill--standard');
  } else {
    const remainingFormatted = '$' + (remaining / 100).toFixed(2);
    textEl.innerHTML = `You're <strong>${remainingFormatted}</strong> away from free shipping`;
    textEl.classList.remove('cart-shipping-bar__text--success');
    fillEl.style.width = pct + '%';
    fillEl.classList.remove('cart-shipping-bar__fill--complete');
    fillEl.classList.remove('cart-shipping-bar__fill--standard');
  }
}

// Patch CartDrawer.renderContents to update shipping bar + upsells after re-render.
// Wrapped in a wait loop because cart-drawer.js may load after this script.
(function patchCartDrawer() {
  // AIOS: also wait for CartDrawer.prototype.renderContents to exist (load-order guard),
  // capped so it can never loop forever, so this patch never throws at page load.
  if (typeof CartDrawer === 'undefined' || !CartDrawer.prototype || typeof CartDrawer.prototype.renderContents !== 'function') {
    if ((patchCartDrawer._tries = (patchCartDrawer._tries || 0) + 1) > 60) return;
    // CartDrawer not defined yet — wait and retry
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', patchCartDrawer, { once: true });
    } else {
      setTimeout(patchCartDrawer, 50);
    }
    return;
  }
  const originalRenderContents = CartDrawer.prototype.renderContents;
  CartDrawer.prototype.renderContents = function (parsedState) {
    originalRenderContents.call(this, parsedState);
    setTimeout(() => {
      updateShippingBar();
      const upsells = document.querySelector('cart-upsells');
      if (upsells) upsells.loadUpsells();
    }, 100);
  };
})();
